import { createFileRoute, Link } from '@tanstack/react-router'
import { MapPinIcon, MessageSquareIcon, UserIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { lineCountKey, locationLabel, useAdjustments } from '@/api/adjustments'
import type { Adjustment } from '@/api/adjustments'
import { VoidedBadge } from '@/components/corrections/voided'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Button } from '@/components/ui/button'

// Adjustments list (UI-SCREENS I-10, #67): date, location, reason, line count and who recorded
// it. Owner and Staff both see it and both may add one (permissions.md). The materials, WIP
// (04l #140), Home and Sales Channel (05e #173) locations; a channel document names its channel.
export const Route = createFileRoute('/w/$slug/adjustments/')({
  component: AdjustmentsList,
  staticData: { breadcrumb: 'adjustments.title' },
})

function AdjustmentsList() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const adjustments = useAdjustments(slug)

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('adjustments.title')}</h1>
        <Button asChild className="h-11">
          <Link to="/w/$slug/adjustments/new" params={{ slug }}>
            {t('adjustments.new')}
          </Link>
        </Button>
      </div>

      {adjustments.isPending ? (
        <LoadingSkeleton />
      ) : adjustments.isError || !adjustments.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : adjustments.data.length === 0 ? (
        <RecordListEmpty>{t('adjustments.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {adjustments.data.map((adjustment) => (
            <AdjustmentRow key={adjustment.id} slug={slug} adjustment={adjustment} />
          ))}
        </RecordList>
      )}
    </section>
  )
}

function AdjustmentRow({ slug, adjustment }: { slug: string; adjustment: Adjustment }) {
  const { t } = useTranslation()

  return (
    <RecordListItem
      voided={Boolean(adjustment.voided_at)}
      title={
        <RecordLink to="/w/$slug/adjustments/$adjustmentId" params={{ slug, adjustmentId: String(adjustment.id) }}>
          {formatBusinessDate(adjustment.occurred_on)}
        </RecordLink>
      }
      badges={<VoidedBadge record={adjustment} />}
      meta={[
        { icon: MapPinIcon, label: t('adjustments.columns.location'), value: locationLabel(t, adjustment.location) },
        {
          icon: MessageSquareIcon,
          label: t('adjustments.columns.reason'),
          value: t(`adjustments.reasons.${adjustment.reason}`),
        },
        adjustment.created_by
          ? {
              icon: UserIcon,
              label: t('adjustments.columns.createdBy'),
              value: adjustment.created_by.name ?? adjustment.created_by.email,
            }
          : null,
      ]}
      figure={t(lineCountKey(adjustment.location.kind), { count: adjustment.lines.length })}
    />
  )
}
